import React from 'react';
import HiresPage from '../components/hires/HiresPage';
import { readInitialHiresView } from '../constants/hiresViewTabs';

/**
 * ADMIN HIRES — every hire line on the instance, across every department.
 * `/admin/hires`, `AdminRoute`-gated (`App.js`), so every viewer is a site admin.
 *
 * 🔑 **This file is a SCOPE DECLARATION, not a page.** The page body is
 * `components/hires/HiresPage.js`, shared with `MyHires.js` and
 * `DepartmentHires.js` (owner, 2026-10-03: *"lets do them all"*).
 *
 * ── 🔀 WHAT CHANGED HERE ON 2026-10-03, AND WHAT WAS CONVERGED ───────────
 *
 * **GAINED** (item B of the unification — the owner asked for the shared
 * toolbar and the Table view):
 * 1. **The shared two-band chrome**, `components/hires/HiresListChrome.js`.
 *    This page rendered its own `flex items-center gap-3 mb-4 flex-wrap` row of
 *    `ui/SearchInput` + `ui/Tabs` and NO `ui/PageHeaderBand` at all, so it was
 *    the one hires page with no context line, no sticky bar and no `Actions ▾`.
 *    🎨 **Design note (the `frontend-design` pass): nothing new was authored.**
 *    A third visual vocabulary on a third hires page is the exact divergence
 *    the owner asked to remove, and `client/src/pages/CLAUDE.md` +
 *    `components/ui/CLAUDE.md` pin the chrome, so the whole palette, type scale
 *    and layout are INHERITED. The one element genuinely written for this page
 *    is its context label, below.
 * 2. **The TABLE view**, with its status tabs, sort, pager, column widths,
 *    `Fit columns to screen`, Export/Print and the per-row expansion into the
 *    order. `sfx_hires_view` is one key shared by all three pages, so a stored
 *    `'table'` now RESOLVES here instead of falling back to Kanban. 🔴 With
 *    NOTHING saved this page still opens on Kanban — see
 *    (since the 2026-10-03 rulings it opens on `readInitialHiresView`, like the
 *    other two — Table on desktop).
 * 3. **In-place order expansion**, `deptWide: true`. See the falsification
 *    below — it needed no server change and no change to `HireOrderExpansion`.
 * 4. The `?hire=` deep link, "open this hire" from a Kanban / Gantt / Calendar
 *    card (`onOpenHire`), and the open-hire highlight kept across views — all
 *    consequences of having a Table to open into.
 * 5. The shared `components/EmptyState` card in place of this page's own
 *    hand-rolled `bg-white rounded-lg shadow p-8` block. **The WORDS are
 *    unchanged**; only the container is now the house one. Declared as a
 *    visual change, not slipped in.
 *
 * **CONVERGED, owner ruling 2026-10-03: all eleven held divergences converged to the recommendation** — it now polls, guards stale requests and double
 * submits, checks `isAdmin` for manage, surfaces a refused calendar (F8), holds
 * the row after an action, drops Gantt on a phone, opens on Table on desktop,
 * opens the HIRE from a card, and uses the shared phone top padding.
 *
 * ── 🕶️ THE IN-PLACE-OPENING HYPOTHESIS, FALSIFIED BY READING THE CODE ─────
 *
 * The brief's hypothesis was that `HireOrderExpansion` assumes a SINGLE ACTIVE
 * DEPARTMENT (`canViewDeptWide` → `useDeptOrderExpansion`, a department row
 * fetch) and so could not serve an admin hire list that spans departments.
 * **It does not, and here is the measurement rather than the reasoning:**
 * - The `deptWide` branch's row fetch is `HireOrderExpansion.js`'s
 *   `fetchDepartmentRow`, which issues `GET /api/orders/department?ids=<id>` —
 *   **it sends no `department_id` and no scope of any kind.**
 * - On the server, `routes/orders.js`'s department-filter block opens
 *   `if (!req.isGlobalAdmin && req.activeDepartment)`, with its own comment
 *   *"Global admins see all orders"* — so for this page's only viewer class the
 *   query is unscoped and `?ids=` returns the order whatever department raised
 *   it. This page's own docblock already recorded that fact for
 *   `handleViewOrder`'s destination; it is the same endpoint.
 * - `useDeptOrderExpansion` resolves its per-control gates per ORDER
 *   (`capabilitiesFor(order.department_id)`) with an `isAdmin` disjunct, not
 *   against one active department.
 * **So: no server change, no change to `HireOrderExpansion` or the order
 * hooks.** `handleViewOrder` is also KEPT (it is still what the Kanban / Gantt /
 * Calendar cards and the money dialog's "& view order" call).
 *
 * ⚠️ **ONE MEASURED CAVEAT, PRE-EXISTING AND REPORTED RATHER THAN FIXED.**
 * `useDeptOrderExpansion`'s cost-code and payment-type effects both open
 * `if (!activeDepartment) { set…([]); return; }`, and a site admin holds no
 * assignments at all, so `activeDepartment` is `null` and the inline editor's
 * two accounting dropdowns come up EMPTY. That is this hook's existing
 * behaviour for an unseated admin on Department Orders too — it is not
 * introduced here — and it degrades EDITING, never opening. Flagged for the
 * owner; fixing it is a change to another agent's files.
 */
export const ADMIN_HIRES_SCOPE = Object.freeze({
  logLabel: 'AdminHires',
  testId: 'admin-hires-page',

  rootClassName: 'p-2.5 max-md:pt-0 max-w-full',

  /**
   * 🎨 The one piece of copy authored for this page. The other two read
   * `My hires · N shown` and `All hires · N shown`, and `All hires` is already
   * spent on ONE department — reusing it here would print a statement that
   * cannot be told apart from Department Hires'. What distinguishes this page is
   * BREADTH, and the count beside it already says how many, so the label says
   * the breadth and nothing else. `client/src/pages/CLAUDE.md`: *"The wording of
   * each contextual number is the PAGE's, and copying Department Orders' would
   * print a FALSE statement."*
   */
  contextLabel: 'All departments',

  /** The Export / print document's title. `departmentName` is '' for an admin. */
  actionsHeaderTitle: 'All Hires',

  loadingMessage: 'Loading all hire data...',

  /** The owner's existing words, now in the house `EmptyState` card. */
  emptyTitle: 'No hire items found',
  emptyDescription: 'Hire items will appear here when orders contain hire equipment.',

  initialView: readInitialHiresView,

  /** A hire-card click opens the HIRE, as on the other two (owner ruling 2026-10-03: all eleven held divergences converged to the recommendation). */
  openHireFromCard: true,

  /**
   * TRUE — a wide view like Department Hires, so the Table carries
   * `Requested by` and uses the FULL column set and key.
   */
  showRequestedBy: true,
  showPhoneSummary: true,
  hideCostBannerOnPhone: false,

  hideGanttOnPhone: true,

  /** The three paths are already in `POLL_ENDPOINTS` (`server/helpers/analyticsEndpointMap.js`). */
  poll: true,

  /** No switcher here, but cross-tab events and `visibilitychange` can overlap two GETs. */
  staleRequestGuard: true,

  submitGuard: true,

  /** F8: an empty deadline calendar would read as "nothing is due back". */
  throwOnCalendarError: true,

  holdRowAfterAction: true,

  /**
   * 🔴 FALSE — this page sends the BARE paths (`/api/hires`), where the other
   * two always append a `?`. Preserved exactly; see `HiresPage.js`'s note.
   */
  alwaysQueryString: false,

  /** No filters at all — every hire, every department. */
  buildQueryString: () => '',

  /** Nothing to re-key on: the request never changes shape. */
  resolveFetchKey: () => null,

  resolveCanQuery: () => true,

  /** `isAdmin`, not a hard `true`: the same answer for every viewer `AdminRoute` admits,
   *  without resting on that route's gate. */
  resolveCanManage: ({ isAdmin }) => Boolean(isAdmin),

  /**
   * TRUE — the Department Orders panel, which an admin's unscoped
   * `GET /api/orders/department?ids=` can reach for any department. See the
   * falsification in this file's docblock.
   */
  resolveDeptWide: () => true,

  /**
   * 🔑 **UNCONDITIONALLY Department Orders** — every viewer here is a site admin
   * and `App.js`'s `DepartmentRoute` admits `isAdmin` unconditionally, so there
   * is no capability branch to make (unlike `MyHires.js`, which is reachable by
   * anyone and must decide per viewer). `/orders/:id` is never the destination.
   * 🔻 **CORRECTED 2026-09-28 — "whatever department" was false for a test/demo
   * order**: the unfiltered list still applies the server's test/demo default
   * (`testDemoOrderClauses`) and an admin never sends `includeDemo`. What makes
   * it true is Department Orders' `?ids=` fallback — an order absent from the
   * list is looked up once and held.
   */
  viewOrderPath: (orderId) => `/department/orders?order=${orderId}`,

  /** An admin sees ALL hires, so accept every cross-tab event. */
  resolveInvalidationDepartmentId: () => null,

  /**
   * 🟰 UNIFIED, with evidence. This page used to publish
   * `{ reason }` with no `departmentId` key at all, where the other two publish
   * `{ departmentId, reason }`. An admin has no active department, so the value
   * is `undefined` either way, and the ONE subscriber of `'hires-changed'`
   * (`hooks/useHiresInvalidation.js`) accepts an event whenever
   * `payload.departmentId == null` — so an absent key and an `undefined` key are
   * indistinguishable to it. Read, not assumed: that hook has a single
   * `subscribe('hires-changed', …)` and this is its only test of the field.
   */
  resolveNotifyDepartmentId: () => undefined,
});

function AdminHires() {
  return <HiresPage scope={ADMIN_HIRES_SCOPE} />;
}

export default AdminHires;
